import { Fragment, type CSSProperties } from 'react';
import { motionAllowed } from '../lib/motion';
import './rolling-number.css';

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
// A number as the formatters write it: digits, with any group and decimal separators between.
const NUMBER = /(\d(?:[\d.,'\u00A0\u202F]*\d)?)/;

/**
 * Text with numbers in it, such as "1,204" or "12 words", whose digits roll to their new values
 * when they change, like an odometer's. Columns count from the right, so the units stay the
 * units as a number grows. Where motion is unwanted, it's just the text.
 */
export function RollingNumber({ value }: { readonly value: string }) {
  if (!motionAllowed()) return value;
  const parts = value.split(NUMBER);
  return (
    <span className="rolling">
      <span aria-hidden="true">
        {parts.map((part, index) => {
          const fromEnd = parts.length - index;
          if (index % 2 === 0) return <Fragment key={`text-${String(fromEnd)}`}>{part}</Fragment>;
          // Digits and separators only: each is a single character.
          const chars = Array.from(part);
          return (
            // Its own left-to-right run, so a number reads the right way round in any language.
            <span key={`number-${String(fromEnd)}`} className="rolling-number">
              {chars.map((char, at) => {
                const place = chars.length - 1 - at;
                return DIGITS.includes(char) ? (
                  <span
                    key={`digit-${String(place)}`}
                    className="rolling-digit"
                    style={{ '--digit': Number(char), '--place': place } as CSSProperties}
                  >
                    <span className="rolling-digit-sizer">{char}</span>
                    <span className="rolling-digit-strip">
                      {DIGITS.map((digit) => (
                        <span key={digit}>{digit}</span>
                      ))}
                    </span>
                  </span>
                ) : (
                  <span key={`mark-${String(place)}`}>{char}</span>
                );
              })}
            </span>
          );
        })}
      </span>
      <span className="sr-only">{value}</span>
    </span>
  );
}
